import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { AlertTriangle, ArrowRight, Plus, Search } from "react-feather";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Fuse from "fuse.js";
import {
  Banner,
  Button,
  Input,
  ListInput,
  PageWrapperTwoColumn,
  Section,
  Select,
  SelectOption,
  Spinner,
} from "@cinesys/ui/components";
import SpotterHeader from "../SpotterHeader";
import SpotterChangesPanel, {
  SpotterChangeGroup,
  SpotterChangesPanelProps,
} from "../SpotterChangesPanel";
import SpotterEditedTag from "../SpotterEditedTag";
import SpotterNewTag from "../SpotterNewTag";
import TokenChip, { TokenCarousel, TokenRow } from "../TokenChip";
import CollapsibleFieldRow from "../CollapsibleFieldRow";
import AddOptionSelect from "../AddOptionSelect";
import { SPOTTER_STRINGS } from "../strings";
import { usePersistedState } from "../usePersistedState";
import { combineSpotterQueryErrors } from "../accessError";
import { useInterfaceAccess } from "../../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import { SPOTTER_INTERFACE_ID } from "../config";
import {
  filterTemplateTokenNames,
  insertTemplateToken,
  openTemplateTokenQuery,
} from "../templateTokens";
import {
  CommentDestination,
  Contract,
  ContractField,
  IconikMetadataView,
  MappingMode,
  getCommentDestination,
  getContract,
  getIconikViews,
} from "../mapping/service";
import {
  COMMENT_DESTINATION_FIELD_NAME,
  COMMENT_DESTINATION_KEY,
} from "./commentDestination";
import {
  EligibleFieldOption,
  UnifiedFieldEntry,
  getEligibleFields,
  saveFields,
  useFields,
} from "./service";
import {
  buildAddFieldOptions,
  eligibleFieldTypeLabel,
  FlatFieldEntry,
} from "./addField";
import { FIELD_TYPE_LABELS, summarizeRow } from "./rowSummary";
import { dk, parseDk } from "./fieldKey";
import ConditionalRuleRow, {
  ConditionalRuleFieldOption,
  LocalConditionalRule,
} from "./ConditionalRuleRow";
import {
  CombinedFieldMode,
  COMBINED_FIELD_MODES,
  combinedModeFor,
  mappingModeFor,
  unifiedTypeFor,
} from "./combinedType";
import { MODE_COLORS, ModeDot } from "./modeColors";

/**
 * Spotter unified field tab — merges Field Mapping and Glossary & AI Context into ONE flat,
 * searchable, collapsible field list, replacing their separate tabs/routes. A field's Mode
 * (`CombinedFieldMode` — Direct/Substitute/Format/Conditional/Glossary, see `combinedType.ts`) is
 * a single flat `Select` on every resolved row (full width, first thing in the panel) — Addison:
 * "the user shouldn't really have to know that anything is like a separate concept per se", i.e.
 * no Mapped-vs-Glossary split surfaced anywhere as its own idea, just one "Mode" choice. It's
 * freely switchable in place (`handleTypeChange`) — no Remove-then-Add-again round trip needed,
 * since the full replace-by-diff `PUT` tolerates changing a row's Mode directly. Mutual
 * exclusivity still holds regardless: a field is exactly one Mode at a time.
 *
 * Facial Recognition person-routing has its own tab (`../facialRecognition/FrScreen.tsx`) and its
 * own independent save (`../frMapping/service.ts`'s `useSaveFrMapping`) — this screen neither
 * reads nor writes it.
 *
 * Storage/API: `../mapping/service.ts` (Field Mapping's storage, UNCHANGED — still used by the
 * Rename tab) and `../glossary/service.ts` (deleted, no other consumer) are both superseded here
 * by `./service.ts`'s single `GET`/`PUT /api/spotter/fields` — see that backend route's own doc
 * comment for the byte-compatibility story with the legacy n8n-facing routes this does NOT
 * replace (`GET /mapping`, `GET /glossary/terms` stay exactly as they were).
 *
 * ADD-A-FIELD PATTERN: "Add Field" is `../AddOptionSelect.tsx`'s shared `AddOptionSelect` — a
 * `Select` styled to look like a green button, opening the field-picker menu directly (every
 * not-yet-added field across both views, `addFieldOptions`); picking one resolves the row
 * IMMEDIATELY as Mapped/Direct, no separate pending-row step. The newly-added row's own Mode
 * `Select` (the same one every row has) is where you'd pick Glossary/etc. instead — picking the
 * field IS the add action, and whatever row it creates is immediately a normal, fully editable
 * row like any other (Addison: "the user shouldn't really have to know that anything is like a
 * separate concept" applies here too — no separate not-yet-a-real-row state to reason about).
 *
 * ROW SHELL: each row's collapsible chrome (chevron, label/badge/summary header, Remove button,
 * expand/collapse animation) is `../CollapsibleFieldRow.tsx`'s shared `CollapsibleFieldRow` —
 * this file only supplies the mode-specific badge/summary and the panel content per row.
 */

const Page = styled.div`
  min-height: 100%;
  color: var(--spotterInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

/** Re-skins every `@cinesys/ui` `Select`/`Button`/`Checkbox` rendered inside this `Wrap` to the
 *  Spotter amber palette — see `FrScreen.tsx`'s equivalent doc comment (this block is copied
 *  verbatim, per `apps/customer-frontend/CLAUDE.md`'s styling convention). */
const Wrap = styled.div`
  padding: 0 22px;
  max-width: 1520px;
  margin: 0 auto;

  --inputBackground: var(--spotterRaised);
  --inputBorder: var(--spotterLine2);
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
  --blue060: var(--spotterAccent);
  --blue100: var(--spotterAccent);
  --selectOptionHover: var(--spotterAccentHover);

  --blue400: var(--spotterAccent);
  --blue500: var(--spotterAccent);
  --blueBorder: var(--spotterAccentLine);
  --blueBorderOverlay: var(--spotterAccentLine);
  --blueBackground: var(--spotterAccentSoft);
  --blueFocusRing: var(--spotterAccentSoft);

  --green400: var(--spotterOk);
  --green500: var(--spotterOk);
  --greenBorder: var(--spotterOkLine);
  --greenBorderOverlay: var(--spotterOkLine);
  --greenBackground: var(--spotterOkSoft);

  --red400: var(--spotterDanger);
  --red500: var(--spotterDanger);
  --redBorder: var(--spotterDangerBorder);
  --redBorderOverlay: var(--spotterDangerBorder);
  --redBackground: var(--spotterDangerSoft);
`;

const Main = styled.main`
  padding-top: 20px;
  padding-bottom: 100px;
`;

const SelectPortalHost = styled.div`
  display: contents;
`;

const Note = styled.div`
  display: flex;
  gap: 12px;
  padding: 13px 15px;
  border-radius: 10px;
  background: var(--spotterAccentSoft);
  border: 1px solid var(--spotterAccentLine);
  margin-bottom: 6px;
  font-size: 14px;
  color: var(--spotterInk);
  line-height: 1.5;
`;

const NoteRule = styled.div`
  width: 3px;
  border-radius: 3px;
  background: var(--spotterAccent);
  flex: 0 0 auto;
`;

const HeaderProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderProgressLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--spotterHeaderMuted);
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`;

const HeaderProgressBar = styled.div`
  width: 120px;
  height: 6px;
  border-radius: 99px;
  background: var(--spotterHeaderBarTrack);
  overflow: hidden;
`;

const HeaderProgressBarFill = styled.i<{ $pct: number }>`
  display: block;
  height: 100%;
  width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
  background: var(--spotterAccentBright);
  transition: width 0.3s;
`;

const HeaderProgressCount = styled.b`
  color: #fff;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  white-space: nowrap;
`;

const SearchAddRow = styled.div`
  position: sticky;
  top: var(--spotterHeaderHeight, 98px);
  z-index: 5;
  background: var(--spotterPaper);
  margin: 0 -10px;
  padding: 10px 10px;

  @media (max-width: 700px) {
    position: static;
  }
`;

const SearchAddRowBody = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrap = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const NoFieldsMessage = styled.p`
  padding: 40px 15px;
  margin: 0;
  text-align: center;
  font-size: 14px;
  color: var(--spotterInkSoft);
`;

const FieldRows = styled.div<{ $disabled: boolean }>`
  margin-top: 2px;

  ${(p) =>
    p.$disabled &&
    `
    pointer-events: none;
    opacity: 0.6;
  `}
`;

/** Per-mode row badge (Addison: "some kind of drawn tag... similar to how we do things elsewhere
 *  in the UI... color code those") — replaces the old literal `"[Mapped]"` bracket text
 *  (`rowSummary.ts`'s now-bare `summarizeRow`) with a colored pill, one color PER MODE
 *  (`MODE_COLORS`, `modeColors.ts` — the same six-color set used by the Mode dropdown and the
 *  page's intro copy) so all six modes read distinctly at a glance without opening the row. The
 *  TEXT shown is the Mode (`SPOTTER_STRINGS.fields.modeOptions[combinedModeFor(entry)].label`,
 *  see the render call below), not the underlying type — a Mapped-family row says
 *  "Direct"/"Substitute"/"Format"/"Conditional", not a blanket "Mapped". */
const TypeTag = styled.span<{ $mode: CombinedFieldMode }>`
  flex: 0 0 auto;
  margin-left: 5px;
  color: ${(p) => MODE_COLORS[p.$mode].fg};
  background: ${(p) => MODE_COLORS[p.$mode].bg};
  border: 1px solid ${(p) => MODE_COLORS[p.$mode].line};
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-family: ui-monospace, Menlo, monospace;
  white-space: nowrap;
`;

/** Separator between the before/after `TypeTag`s on a Changes-panel mode-change item — muted
 *  against the two colored tags on either side of it. */
const ModeArrow = styled.span`
  color: var(--spotterInkFaint);
  font-size: 12px;
  flex-shrink: 0;
`;

/** `TypeTag` reused as a Changes-panel item's own badge — `TypeTag`'s `margin-left` is tuned for
 *  sitting right after inline row-list text; the Changes panel's `ItemLabel` already supplies an
 *  even flex `gap`, so that margin would just stack on top of it. */
const ItemTypeTag = styled(TypeTag)`
  margin-left: 0;
`;

const SelectRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 10px;

  @media (min-width: 900px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const SourceFieldWrap = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

/** The Mode `Select` — always the first thing shown in a resolved row's panel, above whatever
 *  mode-specific editor follows (Direct/Substitute/Format's source picker, Glossary's term list).
 *  Full width, not a capped-width compact picker. */
const ModeSelectWrap = styled.div`
  margin-bottom: 12px;
`;

const PanelHint = styled.p`
  font-size: 14px;
  color: var(--spotterInkSoft);
  margin: 0 0 11px;
  line-height: 1.5;
`;

/** Any non-Glossary row's drop-down-destination caveat (`fields.dropdownWarning`, rendered above
 *  the Mode `Select` — every other mode writes to the destination field). A drop-down field,
 *  unlike tag_cloud, only accepts values already configured as options on it, so this gets its
 *  own visually distinct (amber) callout rather than blending into `PanelHint`'s neutral copy.
 *  Glossary is exempt: it only writes terms already drawn from the field's own option list (see
 *  `typeOptionsFor`'s eligibility check), so it can't produce an
 *  out-of-options value in the first place. */
const WarningHint = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: 13px;
  color: var(--spotterAccent);
  background: var(--spotterAccentSoft);
  border: 1px solid var(--spotterAccentLine);
  border-radius: 6px;
  padding: 8px 10px;
  margin: 0 0 11px;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const ValueMapGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 20px 1fr;
  gap: 6px 10px;
  align-items: center;
`;

const ValueMapOld = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  background: var(--spotterCodeBg);
  padding: 5px 9px;
  border-radius: 6px;
`;

const ValueMapArrow = styled.div`
  display: flex;
  justify-content: center;
  color: var(--spotterInkFaint);
`;

const ValueMapRowFields = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PreviewLine = styled.div`
  margin-top: 9px;
  padding: 9px 12px;
  border-radius: 8px;
  background: var(--spotterPaper);
  color: #e6eaee;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 14px;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const PreviewLabel = styled.span`
  color: #8f989f;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const PreviewCaption = styled.p`
  font-size: 14px;
  color: var(--spotterInkSoft);
  margin: 6px 0 0;
  line-height: 1.5;
`;

/** Glossary term-editing row. `$isLast` (set explicitly off the filtered/mapped term array, not
 *  `&:last-child`) drops the
 *  divider off the last VISIBLE term row — `:last-child` doesn't work here since `ExcludedTagsWrap`
 *  always renders right after these as a sibling, so the last `TermRow` is never actually its
 *  parent's last child regardless of how many (non-excluded) terms there are. */
const TermRow = styled.div<{ $isLast?: boolean }>`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 14px;
  align-items: start;
  padding: 10px 0;
  border-bottom: 1px solid var(--spotterLine);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }

  ${(p) =>
    p.$isLast &&
    css`
      border-bottom: none;
    `}
`;

const TermBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const TermBadge = styled.span<{ $filled: boolean }>`
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 660;
  font-size: 14px;
  background: ${(p) =>
    p.$filled ? "var(--spotterOkSoft)" : "var(--spotterCodeBg)"};
  color: ${(p) => (p.$filled ? "var(--spotterOk)" : "var(--spotterInkSoft)")};
  padding: 6px 10px;
  border-radius: 7px;
  display: inline-block;
`;

const TermLabel = styled.div`
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  margin-bottom: 4px;
`;

/** A visual "footer" for the exclusion editor — always the last thing in a panel, set off from
 *  whatever's above it (source picker, template, Glossary's term rows...) with a top border, the
 *  same divider `TermRow` already uses between term rows. Glossary's own last VISIBLE `TermRow`
 *  has its `border-bottom` explicitly dropped (`$isLast`, see `TermRow`'s own doc comment), so
 *  this never doubles up into two adjacent lines — exactly one divider sits between the last term
 *  and this section. */
const ExcludedTagsWrap = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--spotterLine);
`;

/* --- aside: Preview + Unused sources --- */

const Aside = styled.aside`
  padding-top: 20px;
  padding-bottom: 20px;

  @media (min-width: 1030px) {
    position: sticky;
    top: var(--spotterHeaderHeight, 98px);
    max-height: calc(100vh - var(--spotterHeaderHeight, 98px));
    overflow-y: auto;
    margin: 0 -14px;
    padding: 20px 14px 20px;
  }
`;

const AsideChangesPanel = styled.div`
  display: none;

  @media (min-width: 1030px) {
    display: block;
  }
`;

const CountEm = styled.em`
  font-style: normal;
  color: var(--spotterAccent);
  font-size: 12px;
`;

const PreviewRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: start;
  gap: 4px 12px;
  padding: 11px 15px;
  border-bottom: 1px solid var(--spotterLine);

  &:last-child {
    border-bottom: none;
  }
`;

const PreviewField = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  font-weight: 660;
  color: var(--spotterInk);
  overflow-wrap: break-word;
  word-break: break-word;
`;

const PreviewValues = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 5px;
`;

const TagChip = styled.span`
  background: var(--spotterAccentSoft);
  color: var(--spotterAccent);
  border: 1px solid var(--spotterAccentLine);
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 13px;
  font-family: ui-monospace, Menlo, monospace;
`;

const PreviewEmpty = styled.p`
  padding: 14px 15px;
  margin: 0;
  font-size: 13px;
  color: var(--spotterInkSoft);
`;

const TrayGroup = styled.div`
  border-bottom: 1px solid var(--spotterLine);

  &:last-child {
    border-bottom: none;
  }
`;

const TrayGroupLabel = styled.div`
  padding: 10px 15px 0;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
`;

const Tray = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 15px 12px;
`;

const TrayChip = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  background: var(--spotterCodeBg);
  color: var(--spotterInkSoft);
  border: 1px solid var(--spotterLine2);
  border-radius: 99px;
  padding: 3px 9px;
`;

const FIELDS_CONTRACT_VERSION = "2.0.0";

const normalizeFrom = (from: string | string[] | undefined): string[] =>
  !from ? [] : Array.isArray(from) ? from : [from];

type LocalMode = MappingMode | "unmapped";

interface LocalMappedEntry {
  type: "mapped";
  /** Set for the one always-available "Comments" destination (Seth Tanner, Monday 12926430722)
   *  — see `commentDestination.ts`'s doc comment. Absent for an ordinary view/field row. */
  destination?: "comment";
  sources: string[];
  mode: LocalMode;
  value_map: Record<string, string>;
  template: string;
  /** `mode === "conditional"` only. */
  rules: LocalConditionalRule[];
  excludedTags: string[];
}
interface LocalGlossaryEntry {
  type: "glossary";
  terms: Record<string, string>;
  excludedTags: string[];
}
type LocalEntry = LocalMappedEntry | LocalGlossaryEntry;

const emptyConditionalRule = (): LocalConditionalRule => ({
  when: "",
  set: "",
  mode: "overwrite",
});

const emptyMapped = (mode: MappingMode = "direct"): LocalMappedEntry => ({
  type: "mapped",
  sources: [],
  mode,
  value_map: {},
  template: "",
  rules: mode === "conditional" ? [emptyConditionalRule()] : [],
  excludedTags: [],
});
const emptyGlossary = (terms: string[]): LocalGlossaryEntry => ({
  type: "glossary",
  terms: Object.fromEntries(terms.map((t) => [t, ""])),
  excludedTags: [],
});

const COMBINED_MODE_OPTIONS: SelectOption[] = COMBINED_FIELD_MODES.map((m) => ({
  value: m,
  // @cinesys/ui's Select has no `swatch` prop (that was the old local Select's own feature) —
  // its Option.label accepts a ReactNode, so the color dot is baked directly into the label
  // instead, rendering in both the closed control and the open menu the same way plain text would.
  label: (
    <>
      <ModeDot $mode={m} />
      {SPOTTER_STRINGS.fields.modeOptions[m].label}
    </>
  ),
  extra: SPOTTER_STRINGS.fields.modeOptions[m].extra,
}));

/** `eligibleTermsByField` (iconik field name -> its live option list) backfills every currently-
 *  eligible term into a Glossary entry's local `terms`, blank by default, before overlaying
 *  whatever the server actually returned. `GET /fields` only echoes back whatever's in this
 *  field's STORED term map (`fields.ts`'s `GET` handler) — which, for data saved before a term
 *  was ever given a definition (or saved by the old, pre-unification Glossary screen), can be a
 *  sparse subset of the field's real option list. Without this backfill the editor would only
 *  show rows for terms someone already typed something into, instead of the field's full,
 *  current option list (every OTHER type already always shows its full option list). */
const deriveEntries = (
  fields: UnifiedFieldEntry[],
  eligibleTermsByField: Map<string, string[]> = new Map()
): Record<string, LocalEntry> => {
  const result: Record<string, LocalEntry> = {};
  for (const f of fields) {
    const isComment = f.type === "mapped" && f.destination === "comment";
    const scope = f.type === "glossary" ? "asset" : f.scope ?? "asset";
    const key = isComment
      ? COMMENT_DESTINATION_KEY
      : dk(f.to_view_id ?? "", scope, f.to_field_id ?? "");
    if (f.type === "mapped") {
      result[key] = {
        type: "mapped",
        destination: f.destination,
        sources: normalizeFrom(f.from),
        mode: f.mode,
        value_map: f.value_map ?? {},
        template: f.template ?? "",
        rules: (f.rules ?? []).map((r) => ({
          when: r.when,
          set: r.then.set,
          mode: r.then.mode,
        })),
        excludedTags: f.excludedTags ?? [],
      };
    } else {
      const liveTerms =
        eligibleTermsByField.get(f.to_field_id) ?? Object.keys(f.terms);
      const terms: Record<string, string> = Object.fromEntries(
        liveTerms.map((t) => [t, ""])
      );
      Object.assign(terms, f.terms);
      result[key] = {
        type: "glossary",
        terms,
        excludedTags: f.excludedTags ?? [],
      };
    }
  }
  return result;
};

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().join(" ") === [...b].sort().join(" ");

const recordsEqual = (
  a: Record<string, string>,
  b: Record<string, string>
): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return (
    aKeys.length === bKeys.length && aKeys.every((k) => a[k] === (b[k] ?? ""))
  );
};

const rulesEqual = (
  a: LocalConditionalRule[],
  b: LocalConditionalRule[]
): boolean =>
  a.length === b.length &&
  a.every(
    (r, i) => r.when === b[i].when && r.set === b[i].set && r.mode === b[i].mode
  );

/** A field counts as changed when it was added, removed, or its content differs from the
 *  last-saved baseline — unlike the old Mapping screen, "removed" is a real absent key here (no
 *  leftover "unmapped" placeholder), so presence-mismatch alone is enough to flag add/remove. */
const entriesEqual = (
  a: LocalEntry | undefined,
  b: LocalEntry | undefined
): boolean => {
  if (!a && !b) {
    return true;
  }
  if (!a || !b || a.type !== b.type) {
    return false;
  }
  if (a.type === "mapped" && b.type === "mapped") {
    if (a.mode !== b.mode || !arraysEqual(a.sources, b.sources)) {
      return false;
    }
    if (a.mode === "format" && a.template !== b.template) {
      return false;
    }
    if (a.mode === "match" && !recordsEqual(a.value_map, b.value_map)) {
      return false;
    }
    if (a.mode === "conditional" && !rulesEqual(a.rules, b.rules)) {
      return false;
    }
    return arraysEqual(a.excludedTags, b.excludedTags);
  }
  if (a.type === "glossary" && b.type === "glossary") {
    return (
      recordsEqual(a.terms, b.terms) &&
      arraysEqual(a.excludedTags, b.excludedTags)
    );
  }
  return false;
};

export const scopeMatches = (
  fieldScope: ContractField["scope"],
  viewScope: "asset" | "segment"
): boolean => fieldScope === "asset+segment" || fieldScope === viewScope;

const groupContractFields = (
  fields: ContractField[] | undefined
): [string, ContractField[]][] => {
  const groups = new Map<string, ContractField[]>();
  for (const field of fields ?? []) {
    const key = field.group || "other";
    const list = groups.get(key) ?? [];
    list.push(field);
    groups.set(key, list);
  }
  return Array.from(groups.entries());
};

const toTitleCase = (raw: string): string =>
  raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map((w) =>
      /^[A-Z0-9]+$/.test(w)
        ? w
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(" ");

export const toSourceOptions = (cFields: ContractField[]): SelectOption[] =>
  groupContractFields(cFields).flatMap(([group, groupFields]) =>
    groupFields.map((f) => ({
      value: f.key,
      label: f.label,
      group: toTitleCase(group),
    }))
  );

/** Which Source options a field row offers: Comments isn't tied to any view/scope so it gets the
 *  full catalogue, every other row gets its view's asset/segment-scoped bucket as before. */
export const sourceOptionsForKey = (
  key: string,
  viewScope: "asset" | "segment",
  sourceOptionsByScope: Record<"asset" | "segment", SelectOption[]>,
  commentSourceOptions: SelectOption[]
): SelectOption[] =>
  key === COMMENT_DESTINATION_KEY
    ? commentSourceOptions
    : sourceOptionsByScope[viewScope];

const sampleValueFor = (
  field: ContractField | undefined
): string | undefined => {
  if (!field) {
    return undefined;
  }
  const sample = field.sample;
  if (sample !== undefined) {
    return typeof sample === "string" ? sample : sample.value;
  }
  if (Array.isArray(field.valueEnum)) {
    return field.valueEnum[0];
  }
  return undefined;
};

const sampleVarsFor = (
  field: ContractField | undefined
): Record<string, string> | undefined => {
  if (!field) {
    return undefined;
  }
  const sample = field.sample;
  return sample && typeof sample === "object" ? sample : undefined;
};

/** One-value-per-source result for a Mapped entry, applying its mode. */
const resultForMapped = (
  entry: LocalMappedEntry,
  contractByKey: Record<string, ContractField>
): string[] => {
  const { sources, mode } = entry;
  if (mode === "conditional") {
    // Illustrative only — which rule actually fires depends on real asset data, evaluated
    // server-side; the Preview card just lists every rule's own set-to value.
    return Array.from(
      new Set(entry.rules.map((r) => r.set.trim()).filter((s) => s !== ""))
    );
  }
  if (!sources.length || mode === "unmapped") {
    return [];
  }
  if (mode === "match") {
    const sample = sampleValueFor(contractByKey[sources[0]]);
    if (sample === undefined) {
      return [];
    }
    const mapped = entry.value_map[sample] ?? sample;
    return mapped ? [mapped] : [];
  }
  if (mode === "format") {
    if (!entry.template) {
      return [];
    }
    let filled = entry.template;
    if (sources.length === 1) {
      const field = contractByKey[sources[0]];
      const vars = sampleVarsFor(field) ?? {
        value: sampleValueFor(field) ?? "",
      };
      Object.entries(vars).forEach(([token, value]) => {
        filled = filled.split(`{${token}}`).join(value);
      });
    } else {
      sources.forEach((s) => {
        filled = filled
          .split(`{${s}}`)
          .join(sampleValueFor(contractByKey[s]) ?? "");
      });
    }
    filled = filled
      .replace(/\{[^}]+\}/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return filled ? [filled] : [];
  }
  const vals = sources
    .map((s) => sampleValueFor(contractByKey[s]))
    .filter((v): v is string => v !== undefined && v !== "");
  return Array.from(new Set(vals));
};

const formatTokensFor = (
  sources: string[],
  contractByKey: Record<string, ContractField>
): string[] => {
  if (sources.length === 1) {
    const vars = contractByKey[sources[0]]?.formatVars;
    return vars && vars.length > 0
      ? ["{value}", ...vars.map((v) => `{${v}}`)]
      : ["{value}"];
  }
  return sources.map((s) => `{${s}}`);
};

interface ValueMapRowValue {
  from: string;
  to: string;
}

const ValueMapRow: React.FC<{
  value: ValueMapRowValue;
  onChange: (value: ValueMapRowValue) => void;
}> = ({ value, onChange }) => (
  <ValueMapRowFields>
    <Input
      noPadding
      minWidth={0}
      placeholder={SPOTTER_STRINGS.fields.matching.addRowFromPlaceholder}
      value={value.from}
      onChange={(from) => onChange({ ...value, from })}
    />
    <ArrowRight size={14} />
    <Input
      noPadding
      minWidth={0}
      placeholder={SPOTTER_STRINGS.fields.matching.addRowToPlaceholder}
      value={value.to}
      onChange={(to) => onChange({ ...value, to })}
    />
  </ValueMapRowFields>
);

const FieldsScreen = () => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  const queryClient = useQueryClient();

  const contractQuery = useQuery<Contract>("spotter-contract", getContract);
  const viewsQuery = useQuery<IconikMetadataView[]>(
    "spotter-views",
    getIconikViews
  );
  const eligibleQuery = useQuery<EligibleFieldOption[]>(
    "spotter-eligible-fields",
    getEligibleFields
  );
  // Fixed descriptor, not per-org — see `getCommentDestination`'s own doc comment — but still
  // fetched through the normal react-query path (this app's house convention, see
  // `apps/customer-frontend/CLAUDE.md`'s "Data fetching" section) rather than a bare constant, so
  // it goes through the same access-gating/error handling every other Spotter query does.
  const commentDestinationQuery = useQuery<CommentDestination>(
    "spotter-comment-destination",
    getCommentDestination
  );
  const fieldsQuery = useFields();

  const [fields, setFields] = usePersistedState<Record<string, LocalEntry>>(
    "spotter-fields",
    {}
  );
  const [originalFields, setOriginalFields] = usePersistedState<
    Record<string, LocalEntry>
  >("spotter-fields-original", {});
  const [order, setOrder] = usePersistedState<string[]>(
    "spotter-fields-order",
    []
  );
  const [initialized, setInitialized] = usePersistedState(
    "spotter-fields-initialized",
    false
  );
  const [selectPortal, setSelectPortal] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(new Set());
  const [justAddedKey, setJustAddedKey] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const templateInputRefs = useRef<
    Record<string, HTMLInputElement | HTMLTextAreaElement | null>
  >({});

  // Scrolls a freshly-added row into view — its Mode `Select` gets keyboard focus via its own
  // `autoFocus` prop (set on mount below, keyed off `justAddedKey`), so this only needs to handle
  // scroll position, not focus itself.
  React.useEffect(() => {
    if (!justAddedKey) {
      return;
    }
    rowRefs.current[justAddedKey]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    setJustAddedKey(null);
  }, [justAddedKey]);

  // The expand/collapse height animation itself lives in `CollapsibleFieldRow` (each row owns its
  // own panel ref) — this just flips which keys are expanded.
  const toggleExpanded = (key: string): void => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  React.useEffect(() => {
    if (
      !initialized &&
      fieldsQuery.data &&
      viewsQuery.data &&
      eligibleQuery.data
    ) {
      const initial = deriveEntries(
        fieldsQuery.data.fields,
        new Map(eligibleQuery.data.map((f) => [f.name, f.terms]))
      );
      setFields(initial);
      setOriginalFields(initial);

      // Initial order: alphabetical by label across every type (a flat, unsegmented list — the
      // unification design explicitly calls for NOT grouping by type).
      const labelFor = (key: string): string => {
        const parsed = parseDk(key);
        if (!parsed) {
          return key;
        }
        const entry = initial[key];
        if (entry.type === "glossary") {
          return (
            eligibleQuery.data?.find((f) => f.name === parsed.fieldName)
              ?.label ?? parsed.fieldName
          );
        }
        const view = viewsQuery.data?.find((v) => v.id === parsed.viewId);
        const field = view?.view_fields.find(
          (f) => f.name === parsed.fieldName
        );
        return field?.label || parsed.fieldName;
      };
      setOrder(
        Object.keys(initial).sort((a, b) =>
          labelFor(a).localeCompare(labelFor(b))
        )
      );
      setInitialized(true);
    }
  }, [initialized, fieldsQuery.data, viewsQuery.data, eligibleQuery.data]);

  const eligibleByName = new Map(
    (eligibleQuery.data ?? []).map((f) => [f.name, f])
  );

  const saveMutation = useMutation(
    (vars: { contract_version: string; fields: UnifiedFieldEntry[] }) =>
      saveFields(vars),
    {
      onSuccess: (result) => {
        queryClient.setQueryData("spotter-fields", result);
        const saved = deriveEntries(
          result.fields,
          new Map((eligibleQuery.data ?? []).map((f) => [f.name, f.terms]))
        );
        setFields(saved);
        setOriginalFields(saved);
      },
    }
  );

  // Org-level entitlement gate — checked BEFORE this screen's own query loading/error states
  // below (a per-user ACL denial, a different concern; see `combineSpotterQueryErrors`'s doc
  // comment). "pending"/"denied" both keep `SpotterHeader`'s placeholder chrome; "pending" shows
  // `DelayedSpinner` (matches AB Vault's `ScreenShell`, unified — see its own doc comment),
  // "denied" swaps in the shared notice instead, in place of this screen's real content.
  if (access.status !== "granted") {
    return (
      <Page>
        <SpotterHeader />
        {access.status === "denied" && (
          <Wrap>
            <InterfaceAccessNotice reason={access.reason} />
          </Wrap>
        )}
        {access.status === "pending" && <DelayedSpinner padTop />}
      </Page>
    );
  }

  const isLoading =
    contractQuery.isLoading ||
    viewsQuery.isLoading ||
    eligibleQuery.isLoading ||
    commentDestinationQuery.isLoading ||
    fieldsQuery.isLoading;

  if (isLoading) {
    return (
      <Page>
        <SpotterHeader />
        <Spinner padTop />
      </Page>
    );
  }

  const { hasError, accessDenied } = combineSpotterQueryErrors([
    contractQuery,
    viewsQuery,
    eligibleQuery,
    commentDestinationQuery,
    fieldsQuery,
  ]);

  if (hasError) {
    return (
      <Page>
        <SpotterHeader />
        <Wrap>
          <Banner
            error={
              accessDenied
                ? SPOTTER_STRINGS.fields.accessDeniedError
                : SPOTTER_STRINGS.fields.loadError
            }
            padTop
          />
        </Wrap>
      </Page>
    );
  }

  const views = viewsQuery.data ?? [];
  const assetView = views.find((v) => v.scope === "asset");
  const segmentView = views.find((v) => v.scope === "segment");
  const fieldEntriesFor = (
    view: IconikMetadataView | undefined
  ): FlatFieldEntry[] =>
    view
      ? (view.view_fields ?? []).map((field) => ({
          view,
          field,
          key: dk(view.id, view.scope, field.name),
        }))
      : [];
  const flatFieldEntries: FlatFieldEntry[] = [
    ...fieldEntriesFor(assetView),
    ...fieldEntriesFor(segmentView),
  ];
  const flatFieldEntryByKey = new Map(
    flatFieldEntries.map((e): [string, FlatFieldEntry] => [e.key, e])
  );

  const contractFields = contractQuery.data?.fields ?? [];
  const contractByKey: Record<string, ContractField> = {};
  for (const f of contractFields) {
    contractByKey[f.key] = f;
  }
  const sourceLabel = (key: string): string => contractByKey[key]?.label ?? key;
  const knownValuesFor = (key: string): string[] => {
    const valueEnum = contractByKey[key]?.valueEnum;
    return Array.isArray(valueEnum) ? valueEnum : valueEnum ? [valueEnum] : [];
  };
  const mappableContractFields = contractFields.filter(
    (f) => f.translation !== "glossary"
  );
  const sourceOptionsByScope: Record<"asset" | "segment", SelectOption[]> = {
    asset: toSourceOptions(
      mappableContractFields.filter((f) => scopeMatches(f.scope, "asset"))
    ),
    segment: toSourceOptions(
      mappableContractFields.filter((f) => scopeMatches(f.scope, "segment"))
    ),
  };
  // Comments aren't tied to any view/scope, so unlike a real field row they get the
  // full catalogue instead of one of the scope-filtered buckets above.
  const commentSourceOptions = toSourceOptions(mappableContractFields);

  // Which destination field NAMES are already used by ANY type — mutual exclusivity means a field
  // used as one type is never offered as an option for another (see `addField.ts`'s doc comment).
  const addedFieldNames = new Set(
    order.map((key) => parseDk(key)?.fieldName).filter((n): n is string => !!n)
  );

  const fieldLabelFor = (key: string): string => {
    if (key === COMMENT_DESTINATION_KEY) {
      return SPOTTER_STRINGS.fields.commentDestinationLabel;
    }
    const parsed = parseDk(key);
    if (!parsed) {
      return key;
    }
    const entry = fields[key];
    if (entry?.type === "glossary") {
      return eligibleByName.get(parsed.fieldName)?.label ?? parsed.fieldName;
    }
    return flatFieldEntryByKey.get(key)?.field.label ?? parsed.fieldName;
  };

  const isFieldNew = (key: string): boolean => !originalFields[key];
  const changedKeys = Array.from(
    new Set([...Object.keys(fields), ...Object.keys(originalFields)])
  ).filter((key) => !entriesEqual(fields[key], originalFields[key]));
  const changedKeySet = new Set(changedKeys);

  const modeLabel = (mode: CombinedFieldMode): string =>
    SPOTTER_STRINGS.fields.modeOptions[mode].label;

  type FieldChangeItem = {
    id: string;
    label: string;
    status?: "added" | "removed" | "edited";
    badge?: React.ReactNode;
  };

  // The item's own mode-color badge (same `TypeTag` swatch as the row list) — used only on items
  // that are actually ABOUT a mode (a field added/removed outright, or switched between modes),
  // never as a blanket badge on the group itself.
  const modeTypeTag = (mode: CombinedFieldMode): React.ReactNode => (
    <ItemTypeTag $mode={mode}>{modeLabel(mode)}</ItemTypeTag>
  );

  // One value gained/lost from a list becomes its own item with an added/removed pill, rather
  // than one sentence naming every value.
  const listDiffItems = (
    idPrefix: string,
    currentList: string[],
    originalList: string[],
    labelFor: (value: string) => string = (v) => v
  ): FieldChangeItem[] => [
    ...currentList
      .filter((v) => !originalList.includes(v))
      .map((v) => ({
        id: `${idPrefix}:${v}`,
        label: labelFor(v),
        status: "added" as const,
      })),
    ...originalList
      .filter((v) => !currentList.includes(v))
      .map((v) => ({
        id: `${idPrefix}:${v}`,
        label: labelFor(v),
        status: "removed" as const,
      })),
  ];

  // One line per distinct edit on a field, comparing its current local state against its
  // last-saved baseline — mirrors `entriesEqual`'s own comparisons one-for-one. A field that's
  // brand-new or removed outright gets a single summary line instead, since there's no baseline
  // (or no current state) to diff against.
  const fieldChangeItems = (
    key: string,
    current: LocalEntry | undefined,
    original: LocalEntry | undefined
  ): FieldChangeItem[] => {
    if (current && !original) {
      const detail =
        current.type === "mapped" && current.sources.length
          ? current.sources.map(sourceLabel).join(", ")
          : undefined;
      return [
        {
          id: `${key}:added`,
          label: SPOTTER_STRINGS.fields.changes.added(detail),
          badge: modeTypeTag(combinedModeFor(current)),
        },
      ];
    }
    if (!current && original) {
      return [
        {
          id: `${key}:removed`,
          label: SPOTTER_STRINGS.fields.changes.removed,
          badge: modeTypeTag(combinedModeFor(original)),
        },
      ];
    }
    if (!current || !original) {
      return [];
    }
    const items: FieldChangeItem[] = [];
    const oldMode = combinedModeFor(original);
    const newMode = combinedModeFor(current);
    if (oldMode !== newMode) {
      items.push({
        id: `${key}:mode`,
        label: SPOTTER_STRINGS.fields.changes.modeChanged,
        badge: (
          <>
            {modeTypeTag(oldMode)}
            <ModeArrow>→</ModeArrow>
            {modeTypeTag(newMode)}
          </>
        ),
      });
    } else if (current.type === "mapped" && original.type === "mapped") {
      items.push(
        ...listDiffItems(
          `${key}:src`,
          current.sources,
          original.sources,
          sourceLabel
        )
      );
      if (current.mode === "format" && current.template !== original.template) {
        items.push({
          id: `${key}:template`,
          label: SPOTTER_STRINGS.fields.changes.formatTemplateEdited,
          status: "edited",
        });
      }
      if (
        current.mode === "match" &&
        !recordsEqual(current.value_map, original.value_map)
      ) {
        items.push({
          id: `${key}:valueMap`,
          label: SPOTTER_STRINGS.fields.changes.substituteValuesEdited,
          status: "edited",
        });
      }
      if (
        current.mode === "conditional" &&
        !rulesEqual(current.rules, original.rules)
      ) {
        items.push({
          id: `${key}:rules`,
          label: SPOTTER_STRINGS.fields.changes.conditionalRulesEdited,
          status: "edited",
        });
      }
    } else if (current.type === "glossary" && original.type === "glossary") {
      items.push(
        ...listDiffItems(
          `${key}:term`,
          Object.keys(current.terms),
          Object.keys(original.terms)
        )
      );
      const editedTerms = Object.keys(current.terms).filter(
        (t) => t in original.terms && current.terms[t] !== original.terms[t]
      );
      items.push(
        ...editedTerms.map((t) => ({
          id: `${key}:term:${t}`,
          label: t,
          status: "edited" as const,
        }))
      );
    }
    // Every type carries `excludedTags`, independent of which mode a field is in — diff it
    // regardless of whether the mode itself also changed.
    items.push(
      ...listDiffItems(
        `${key}:excl`,
        current.excludedTags,
        original.excludedTags
      )
    );
    return items;
  };

  // One change-panel group per destination FIELD, with every change on it listed underneath as
  // children.
  const changeGroupsByKey = new Map<string, SpotterChangeGroup>();
  for (const key of changedKeys) {
    changeGroupsByKey.set(key, {
      id: key,
      label: fieldLabelFor(key),
      isNew: isFieldNew(key),
      items: fieldChangeItems(key, fields[key], originalFields[key]),
    });
  }
  const changeGroups = Array.from(changeGroupsByKey.values());

  const search_ = search.trim();
  const searchableRows = order.map((key) => {
    const entry = fields[key];
    return {
      key,
      label: fieldLabelFor(key),
      typeLabel: entry ? FIELD_TYPE_LABELS[entry.type] : "",
      sourceLabels:
        entry?.type === "mapped" ? entry.sources.map(sourceLabel) : [],
    };
  });
  const visibleKeys = search_
    ? new Set(
        new Fuse(searchableRows, {
          keys: ["label", "typeLabel", "sourceLabels"],
          threshold: 0.4,
          shouldSort: false,
        })
          .search(search_)
          .map((r) => r.item.key)
      )
    : new Set(order);

  const totalFieldCount = flatFieldEntries.length;
  const mappedCount = order.filter((key) => {
    if (key === COMMENT_DESTINATION_KEY) {
      return false; // Comments isn't one of the real fields totalFieldCount counts.
    }
    const e = fields[key];
    return e?.type === "mapped" && e.sources.length > 0;
  }).length;

  const updateEntry = (
    key: string,
    updater: (cur: LocalEntry) => LocalEntry
  ): void => {
    setFields((prev) => {
      const cur = prev[key];
      if (!cur) {
        return prev;
      }
      return { ...prev, [key]: updater(cur) };
    });
  };

  const handleSourcesChange = (key: string, sources: string[]): void => {
    updateEntry(key, (cur) => {
      if (cur.type !== "mapped") {
        return cur;
      }
      if (cur.mode !== "match") {
        return { ...cur, sources };
      }
      const hasFixedList = knownValuesFor(sources[0] ?? "").length > 0;
      return { ...cur, sources, value_map: hasFixedList ? {} : cur.value_map };
    });
  };
  const handleValueMapChange = (
    key: string,
    from: string,
    to: string
  ): void => {
    updateEntry(key, (cur) => {
      if (cur.type !== "mapped") {
        return cur;
      }
      const value_map = { ...cur.value_map };
      if (to) {
        value_map[from] = to;
      } else {
        delete value_map[from];
      }
      return { ...cur, value_map };
    });
  };
  const handleValueMapListChange = (
    key: string,
    rows: ValueMapRowValue[]
  ): void => {
    updateEntry(key, (cur) =>
      cur.type !== "mapped"
        ? cur
        : {
            ...cur,
            value_map: Object.fromEntries(rows.map((r) => [r.from, r.to])),
          }
    );
  };
  const handleTemplateChange = (key: string, template: string): void => {
    updateEntry(key, (cur) =>
      cur.type !== "mapped" ? cur : { ...cur, template }
    );
  };
  const handleRulesChange = (
    key: string,
    rules: LocalConditionalRule[]
  ): void => {
    updateEntry(key, (cur) =>
      cur.type !== "mapped" ? cur : { ...cur, rules }
    );
  };
  const handleTermChange = (key: string, term: string, value: string): void => {
    updateEntry(key, (cur) =>
      cur.type !== "glossary"
        ? cur
        : { ...cur, terms: { ...cur.terms, [term]: value } }
    );
  };
  const handleExcludedTagsChange = (key: string, tags: string[]): void => {
    updateEntry(key, (cur) => ({ ...cur, excludedTags: tags }));
  };

  const handleSave = (): void => {
    const outFields: UnifiedFieldEntry[] = order
      .map((key): UnifiedFieldEntry | undefined => {
        const entry = fields[key];
        const parsed = parseDk(key);
        if (!entry || !parsed) {
          return undefined;
        }
        const { viewId, viewScope, fieldName } = parsed;
        if (entry.type === "mapped" && entry.mode === "conditional") {
          // `then.field` is always this row's own destination — there's no separate picker for
          // it — and `from` is server-derived from whatever fields the rules' `when` strings
          // reference, so neither is sent here. Whatever's sent for a comment row is discarded
          // and re-derived server-side anyway (`sanitizeConditionalRules`), so `fieldName` (this
          // row's internal sentinel, not a real field id) is harmless to send as-is.
          const rules = entry.rules
            .filter((r) => r.set.trim() !== "")
            .map((r) => ({
              when: r.when,
              then: { field: fieldName, set: r.set, mode: r.mode },
            }));
          if (rules.length === 0) {
            return undefined;
          }
          const out: UnifiedFieldEntry = {
            type: "mapped",
            scope: viewScope,
            from: [],
            mode: "conditional",
            rules,
          };
          if (entry.destination === "comment") {
            out.destination = "comment";
          } else {
            out.to_view_id = viewId;
            out.to_field_id = fieldName;
          }
          if (entry.excludedTags.length) {
            out.excludedTags = entry.excludedTags;
          }
          return out;
        }
        if (entry.type === "mapped") {
          if (entry.sources.length === 0) {
            return undefined;
          }
          const out: UnifiedFieldEntry = {
            type: "mapped",
            scope: viewScope,
            from: entry.sources.length === 1 ? entry.sources[0] : entry.sources,
            mode: entry.mode === "unmapped" ? "direct" : entry.mode,
          };
          if (entry.destination === "comment") {
            out.destination = "comment";
          } else {
            out.to_view_id = viewId;
            out.to_field_id = fieldName;
          }
          if (entry.mode === "match") {
            const value_map = Object.fromEntries(
              Object.entries(entry.value_map).filter(([from]) => from)
            );
            if (Object.keys(value_map).length) {
              out.value_map = value_map;
            }
          }
          if (entry.mode === "format" && entry.template) {
            out.template = entry.template;
          }
          if (entry.mode === "direct" && entry.sources.length > 1) {
            out.merge = "append_unique";
          }
          if (entry.excludedTags.length) {
            out.excludedTags = entry.excludedTags;
          }
          return out;
        }
        const out: UnifiedFieldEntry = {
          type: "glossary",
          to_view_id: viewId,
          to_field_id: fieldName,
          fieldLabel: eligibleByName.get(fieldName)?.label ?? fieldName,
          // An excluded term never sends its definition, even one already on file — exclusion
          // means "never apply this tag," so there is nothing for a saved definition to describe.
          terms: Object.fromEntries(
            Object.entries(entry.terms).filter(
              ([term]) => !entry.excludedTags.includes(term)
            )
          ),
        };
        if (entry.excludedTags.length) {
          out.excludedTags = entry.excludedTags;
        }
        return out;
      })
      .filter((f): f is UnifiedFieldEntry => !!f);

    saveMutation.mutate({
      contract_version: FIELDS_CONTRACT_VERSION,
      fields: outFields,
    });
  };

  const handleCancel = (): void => {
    setFields(originalFields);
    setOrder(
      Array.from(new Set([...order, ...Object.keys(originalFields)])).filter(
        (k) => k in originalFields
      )
    );
    saveMutation.reset();
  };

  const emptyEntryFor = (
    mode: CombinedFieldMode,
    fieldName: string
  ): LocalEntry => {
    if (mode === "glossary") {
      return emptyGlossary(eligibleByName.get(fieldName)?.terms ?? []);
    }
    return emptyMapped(mappingModeFor(mode));
  };
  /** "Add Field" resolves immediately, no separate pending step — `optionValue` is already the
   *  field's own composite key (`FlatFieldEntry.key`, straight off the `Select`'s option value),
   *  so there's no key synthesis to do. The new row starts life as an ordinary Mapped/Direct row;
   *  its own Mode `Select` (shown full-width, first thing in the panel) is where you'd pick
   *  Glossary/etc. instead, exactly the same control an already-resolved row uses
   *  (`handleTypeChange`) — adding a field and changing its mode are the same afterward.
   *
   *  The one exception is the Comments destination (`COMMENT_DESTINATION_KEY`) — it isn't a real
   *  `FlatFieldEntry`, so it starts life as a Mapped/Direct row with `destination: "comment"` set
   *  from the start instead of via `emptyEntryFor`. */
  const handleAddField = (key: string): void => {
    const entry: LocalEntry =
      key === COMMENT_DESTINATION_KEY
        ? { ...emptyMapped("direct"), destination: "comment" }
        : emptyEntryFor("direct", "");
    setFields((prev) => ({ ...prev, [key]: entry }));
    setOrder((prev) => [key, ...prev]);
    setExpandedKeys((prev) => new Set(prev).add(key));
    setJustAddedKey(key);
  };
  /** Switches an already-resolved row's flattened type/mode in place — see the file doc comment's
   *  "ADD-A-FIELD PATTERN" / top paragraph for why type is no longer permanent. Staying within the
   *  Mapped family (e.g. Direct → Substitute) just changes `mode`, preserving sources/value_map/
   *  template; crossing into/out of Glossary resets the entry to that type's empty shape (nothing
   *  to carry over — sources and terms aren't the same kind of data), but always keeps
   *  `excludedTags`, since that's type-agnostic (any eligible destination field can have it). */
  const handleTypeChange = (key: string, mode: CombinedFieldMode): void => {
    const nextType = unifiedTypeFor(mode);
    updateEntry(key, (cur) => {
      if (cur.type === "mapped" && nextType === "mapped") {
        const nextMode = mappingModeFor(mode);
        return {
          ...cur,
          mode: nextMode,
          sources: nextMode === "match" ? cur.sources.slice(0, 1) : cur.sources,
          rules:
            nextMode === "conditional" && cur.rules.length === 0
              ? [emptyConditionalRule()]
              : cur.rules,
        };
      }
      const parsed = parseDk(key);
      const empty = emptyEntryFor(mode, parsed?.fieldName ?? "");
      return { ...empty, excludedTags: cur.excludedTags };
    });
  };
  const handleRemoveField = (key: string): void => {
    setFields((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
    setOrder((prev) => prev.filter((k) => k !== key));
    setExpandedKeys((prev) => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // --- Preview: every configured field, Mapped (resultForMapped) or Glossary (one non-excluded
  // example term), for parity across both types. ---
  const exampleTermFor = (entry: LocalEntry): string[] => {
    if (entry.type === "mapped") {
      return resultForMapped(entry, contractByKey);
    }
    const terms = Object.keys(entry.terms);
    if (!terms.length) {
      return [];
    }
    const example =
      terms.find((t) => !entry.excludedTags.includes(t)) ?? terms[0];
    return [example];
  };
  const previewRows = order
    .map((key) => {
      const entry = fields[key];
      if (!entry) {
        return undefined;
      }
      const values = exampleTermFor(entry);
      if (values.length === 0) {
        return undefined;
      }
      return { key, label: fieldLabelFor(key), values };
    })
    .filter((r): r is { key: string; label: string; values: string[] } => !!r)
    .sort((a, b) => a.label.localeCompare(b.label));

  const usedSources = new Set<string>();
  Object.values(fields).forEach((e) => {
    if (e.type === "mapped") {
      e.sources.forEach((s) => usedSources.add(s));
    }
  });
  const unusedSources = mappableContractFields.filter(
    (f) => !usedSources.has(f.key)
  );
  const unusedSourceGroups = groupContractFields(unusedSources);

  // Conditional mode's `when` field picker/chips offer every Spotter source field for the org,
  // not just ones already mapped elsewhere — a condition can reference any source the org has,
  // whether or not that source also happens to be mapped to a destination.
  const conditionalFieldOptions: ConditionalRuleFieldOption[] =
    mappableContractFields
      .map((f) => ({ key: f.key }))
      .sort((a, b) => a.key.localeCompare(b.key));

  const changesProps: SpotterChangesPanelProps = {
    groups: changeGroups,
    onSave: handleSave,
    onCancel: handleCancel,
    isSaving: saveMutation.isLoading,
    saveError: saveMutation.isError ? SPOTTER_STRINGS.fields.saveError : null,
    saveSuccess: saveMutation.isSuccess,
  };

  // Every not-yet-added field, across both views — "Add Field" doesn't scope this by mode
  // anymore (see `addField.ts`'s doc comment); whatever mode a newly-added row starts as
  // (Mapped/Direct) is just its default, changed via the same per-row Mode `Select` below.
  const addFieldOptions = buildAddFieldOptions(
    flatFieldEntries,
    addedFieldNames,
    {
      asset: SPOTTER_STRINGS.fields.assetMetadataLabel,
      segment: SPOTTER_STRINGS.fields.timedMetadataLabel,
    },
    new Set(eligibleByName.keys()),
    eligibleFieldTypeLabel,
    // Comments (Seth Tanner, Monday 12926430722) belongs to neither configured view — surfaced as
    // its own "Add Field" option only, gated on the backend actually confirming it's available
    // (`commentDestinationQuery`, a fixed descriptor — see that query's own doc comment).
    commentDestinationQuery.data
      ? {
          key: COMMENT_DESTINATION_KEY,
          fieldName: COMMENT_DESTINATION_FIELD_NAME,
          label: SPOTTER_STRINGS.fields.commentDestinationLabel,
          group: SPOTTER_STRINGS.fields.commentDestinationGroupLabel,
        }
      : undefined
  );

  // Glossary only ACTS on a field that has a live option list to match against, so an ineligible
  // field's row can't be switched INTO Glossary mode — but the option still renders (disabled,
  // with copy explaining why) rather than disappearing, so the rule reads as intentional instead
  // of a silent gap in the Mode list.
  const typeOptionsFor = (fieldName: string): SelectOption[] =>
    eligibleByName.has(fieldName)
      ? COMBINED_MODE_OPTIONS
      : COMBINED_MODE_OPTIONS.map((o) =>
          o.value === "glossary"
            ? {
                ...o,
                isDisabled: true,
                extra: SPOTTER_STRINGS.fields.modeOptionUnavailableExtra,
              }
            : o
        );

  /** The tag-exclusion editor — always the LAST input in a row's panel, set off in its own
   *  visually separated section (`ExcludedTagsWrap`'s top border). One control covers Mapped/
   *  Glossary alike; excluding a term here also drives Glossary's own term-row list (filtered out
   *  below) and what `handleSave` sends. Copy says "tags" for Mapped but "terms" for Glossary,
   *  matching that row's own "term"/"terms" vocabulary elsewhere (`glossary.termLabel`) — same
   *  control, different noun. */
  const renderExcludedTagsEditor = (
    key: string,
    fieldName: string,
    excludedTags: string[],
    noun: "tags" | "terms" = "tags"
  ): JSX.Element | null => {
    const terms = eligibleByName.get(fieldName)?.terms ?? [];
    if (!terms.length) {
      return null;
    }
    const copy =
      noun === "terms"
        ? {
            label: SPOTTER_STRINGS.fields.excludedTags.termsLabel,
            placeholder: SPOTTER_STRINGS.fields.excludedTags.termsPlaceholder,
          }
        : {
            label: SPOTTER_STRINGS.fields.excludedTags.tagsLabel,
            placeholder: SPOTTER_STRINGS.fields.excludedTags.tagsPlaceholder,
          };
    return (
      <ExcludedTagsWrap>
        <Select
          label={copy.label}
          isMulti
          isSearchable
          options={terms.map((term) => ({ value: term, label: term }))}
          placeholder={copy.placeholder}
          value={excludedTags}
          onChange={(picked) =>
            handleExcludedTagsChange(
              key,
              (picked as SelectOption[]).map((o) => o.value as string)
            )
          }
          onClear={() => handleExcludedTagsChange(key, [])}
          portalTarget={selectPortal}
          noPadding
        />
      </ExcludedTagsWrap>
    );
  };

  /** Conditional mode's rule list — replaces the Source `Select` entirely (a conditional entry has
   *  no single "source", see `handleSave`'s `from: []`). First-match-wins order, top to bottom;
   *  each row's own live syntax-valid/invalid check is `ConditionalRuleRow`'s job. */
  const renderConditionalRulesEditor = (
    key: string,
    rules: LocalConditionalRule[]
  ): JSX.Element => (
    <div>
      <PanelHint>{SPOTTER_STRINGS.fields.conditional.hint}</PanelHint>
      {conditionalFieldOptions.length === 0 && (
        <PanelHint>
          {SPOTTER_STRINGS.fields.conditional.noFieldsAvailable}
        </PanelHint>
      )}
      {rules.map((rule, index) => (
        <ConditionalRuleRow
          key={index}
          value={rule}
          onChange={(next) =>
            handleRulesChange(
              key,
              rules.map((r, i) => (i === index ? next : r))
            )
          }
          onRemove={() =>
            handleRulesChange(
              key,
              rules.filter((_, i) => i !== index)
            )
          }
          canRemove={rules.length > 1}
          fieldOptions={conditionalFieldOptions}
          portalTarget={selectPortal}
        />
      ))}
      <Button
        label={SPOTTER_STRINGS.fields.conditional.addRuleButton}
        icon={Plus}
        type="success"
        onClick={() =>
          handleRulesChange(key, [...rules, emptyConditionalRule()])
        }
      />
    </div>
  );

  const renderFieldRow = (key: string): JSX.Element | null => {
    const entry = fields[key];
    if (!entry) {
      return null;
    }
    const parsed = parseDk(key);
    if (!parsed) {
      return null;
    }
    const { fieldName, viewScope } = parsed;
    const displayLabel = fieldLabelFor(key);
    const isNew = isFieldNew(key);
    const isEdited = changedKeySet.has(key) && !isNew;
    const expanded = expandedKeys.has(key);
    const isRemoving = removingKeys.has(key);
    const rowSummary = summarizeRow(
      entry.type,
      entry.type === "mapped" ? entry.sources : [],
      sourceLabel,
      entry.type === "mapped" && entry.mode === "conditional"
        ? entry.rules.length
        : undefined
    );
    const eligible = eligibleByName.has(fieldName);

    return (
      <CollapsibleFieldRow
        key={key}
        ref={(el) => {
          rowRefs.current[key] = el;
        }}
        isNew={isNew}
        isEdited={isEdited}
        isRemoving={isRemoving}
        onRemoveTransitionEnd={() => {
          handleRemoveField(key);
          setRemovingKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }}
        expanded={expanded}
        onToggleExpand={() => toggleExpanded(key)}
        expandTitle={SPOTTER_STRINGS.fields.expandRowTitle}
        collapseTitle={SPOTTER_STRINGS.fields.collapseRowTitle}
        label={displayLabel}
        badge={
          <TypeTag $mode={combinedModeFor(entry)}>
            {SPOTTER_STRINGS.fields.modeOptions[combinedModeFor(entry)].label}
          </TypeTag>
        }
        summary={rowSummary}
        headerRightExtra={
          changedKeySet.has(key) &&
          (isNew ? <SpotterNewTag /> : <SpotterEditedTag />)
        }
        removeLabel={SPOTTER_STRINGS.fields.removeFieldButton}
        onRemoveClick={() => setRemovingKeys((prev) => new Set(prev).add(key))}
      >
        {entry.type !== "glossary" &&
          flatFieldEntryByKey.get(key)?.field.field_type === "drop_down" && (
            <WarningHint>
              <AlertTriangle size={14} />
              {SPOTTER_STRINGS.fields.dropdownWarning}
            </WarningHint>
          )}
        <ModeSelectWrap>
          <Select
            label={SPOTTER_STRINGS.fields.modeSelectLabel}
            options={typeOptionsFor(fieldName)}
            value={combinedModeFor(entry)}
            onChange={(option) => {
              const value = (option as SelectOption)?.value as
                | CombinedFieldMode
                | undefined;
              if (value) {
                handleTypeChange(key, value);
              }
            }}
            autoFocus={key === justAddedKey}
            portalTarget={selectPortal}
            noPadding
          />
        </ModeSelectWrap>

        {entry.type === "mapped" && (
          <>
            {entry.mode !== "conditional" && (
              <SelectRow>
                <SourceFieldWrap>
                  <Select
                    label={SPOTTER_STRINGS.fields.sourceSelectLabel}
                    required
                    multiValueHint={
                      entry.mode !== "match"
                        ? SPOTTER_STRINGS.fields.sourceAddMoreHint
                        : undefined
                    }
                    isMulti={entry.mode !== "match"}
                    options={sourceOptionsForKey(
                      key,
                      viewScope,
                      sourceOptionsByScope,
                      commentSourceOptions
                    )}
                    placeholder={SPOTTER_STRINGS.fields.sourcePlaceholder}
                    isSearchable
                    value={
                      entry.mode !== "match"
                        ? entry.sources
                        : entry.sources[0] ?? null
                    }
                    onChange={(picked) => {
                      if (entry.mode !== "match") {
                        handleSourcesChange(
                          key,
                          (picked as SelectOption[]).map(
                            (o) => o.value as string
                          )
                        );
                      } else {
                        const value = (picked as SelectOption)?.value;
                        handleSourcesChange(key, value ? [value] : []);
                      }
                    }}
                    onClear={() => handleSourcesChange(key, [])}
                    portalTarget={selectPortal}
                    noPadding
                  />
                </SourceFieldWrap>
              </SelectRow>
            )}

            {entry.mode === "conditional" &&
              renderConditionalRulesEditor(key, entry.rules)}

            {entry.sources.length > 0 && entry.mode === "match" && (
              <>
                {(() => {
                  const sourceKey = entry.sources[0];
                  const contractField = contractByKey[sourceKey];
                  const known = knownValuesFor(sourceKey);
                  const isGloss = !!contractField?.glossaryField;
                  return (
                    <>
                      <PanelHint>
                        {isGloss
                          ? SPOTTER_STRINGS.fields.matching.hintGlossary(
                              contractField?.glossaryField
                            )
                          : known.length
                          ? SPOTTER_STRINGS.fields.matching.hintKnown(
                              known.length,
                              sourceLabel(sourceKey)
                            )
                          : SPOTTER_STRINGS.fields.matching.hintUnknown(
                              sourceLabel(sourceKey)
                            )}
                      </PanelHint>
                      {known.length > 0 ? (
                        <ValueMapGrid>
                          {known.map((v) => (
                            <React.Fragment key={v}>
                              <ValueMapOld>{v}</ValueMapOld>
                              <ValueMapArrow>
                                <ArrowRight size={14} />
                              </ValueMapArrow>
                              <Input
                                noPadding
                                minWidth={0}
                                placeholder={v}
                                value={entry.value_map[v] ?? ""}
                                onChange={(to) =>
                                  handleValueMapChange(key, v, to)
                                }
                              />
                            </React.Fragment>
                          ))}
                        </ValueMapGrid>
                      ) : (
                        <ListInput
                          component={ValueMapRow}
                          value={Object.entries(entry.value_map).map(
                            ([from, to]) => ({ from, to })
                          )}
                          onChange={(rows) =>
                            handleValueMapListChange(key, rows)
                          }
                          newValue={() => ({ from: "", to: "" })}
                          noun="row"
                          noMargin
                        />
                      )}
                    </>
                  );
                })()}
              </>
            )}

            {entry.sources.length > 0 && entry.mode === "format" && (
              <>
                <Input
                  label={SPOTTER_STRINGS.fields.formatting.templateLabel}
                  noPadding
                  value={entry.template}
                  onChange={(v) => handleTemplateChange(key, v)}
                  placeholder={
                    entry.sources.length === 1
                      ? SPOTTER_STRINGS.fields.formatting
                          .templatePlaceholderSingle
                      : SPOTTER_STRINGS.fields.formatting.templatePlaceholderMulti(
                          entry.sources[0],
                          entry.sources[entry.sources.length - 1]
                        )
                  }
                  ref={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
                    templateInputRefs.current[key] = el;
                  }}
                />
                {(() => {
                  const matchedFormatTokens = filterTemplateTokenNames(
                    formatTokensFor(entry.sources, contractByKey),
                    openTemplateTokenQuery(entry.template)
                  );
                  return (
                    <TokenRow>
                      {matchedFormatTokens.length > 0 ? (
                        <TokenCarousel
                          gap={8}
                          justify="start"
                          fadeColor="var(--spotterSurface)"
                        >
                          {matchedFormatTokens.map((token) => (
                            <TokenChip
                              key={token}
                              token={token}
                              onClick={() => {
                                handleTemplateChange(
                                  key,
                                  insertTemplateToken(entry.template, token)
                                );
                                templateInputRefs.current[key]?.focus();
                              }}
                            />
                          ))}
                        </TokenCarousel>
                      ) : (
                        <Banner placeholder={SPOTTER_STRINGS.noTokenOptions} />
                      )}
                    </TokenRow>
                  );
                })()}
              </>
            )}

            {entry.sources.length > 0 &&
              entry.mode !== "match" &&
              entry.mode !== "conditional" && (
                <PreviewLine>
                  <PreviewLabel>
                    {SPOTTER_STRINGS.fields.previewLabel}
                  </PreviewLabel>
                  {resultForMapped(entry, contractByKey).join(", ") || "—"}
                </PreviewLine>
              )}
            {entry.mode === "direct" && entry.sources.length > 1 && (
              <PreviewCaption>
                {SPOTTER_STRINGS.fields.direct.merged}
              </PreviewCaption>
            )}
            {eligible &&
              renderExcludedTagsEditor(key, fieldName, entry.excludedTags)}
          </>
        )}

        {entry.type === "glossary" && (
          <>
            {Object.keys(entry.terms)
              .filter((term) => !entry.excludedTags.includes(term))
              .map((term, index, visibleTerms) => {
                const value = entry.terms[term] ?? "";
                return (
                  <TermRow
                    key={term}
                    $isLast={index === visibleTerms.length - 1}
                  >
                    <div>
                      <TermLabel>
                        {SPOTTER_STRINGS.fields.glossary.termLabel}
                      </TermLabel>
                      <TermBadgeRow>
                        <TermBadge $filled={!!value.trim()}>{term}</TermBadge>
                      </TermBadgeRow>
                    </div>
                    <div>
                      <TermLabel>
                        {SPOTTER_STRINGS.fields.glossary.definitionLabel}
                      </TermLabel>
                      <Input
                        textarea
                        noPadding
                        value={value}
                        onChange={(v) => handleTermChange(key, term, v)}
                        placeholder={
                          SPOTTER_STRINGS.fields.glossary.definitionPlaceholder
                        }
                        disabled={saveMutation.isLoading}
                      />
                    </div>
                  </TermRow>
                );
              })}
            {renderExcludedTagsEditor(
              key,
              fieldName,
              entry.excludedTags,
              "terms"
            )}
          </>
        )}
      </CollapsibleFieldRow>
    );
  };

  const pct = totalFieldCount > 0 ? (mappedCount / totalFieldCount) * 100 : 0;

  return (
    <Page>
      <SpotterHeader
        changes={changesProps}
        headerExtra={
          totalFieldCount > 0 ? (
            <HeaderProgress>
              <HeaderProgressLabel>
                {SPOTTER_STRINGS.fields.progressLabel}
              </HeaderProgressLabel>
              <HeaderProgressBar>
                <HeaderProgressBarFill $pct={pct} />
              </HeaderProgressBar>
              <HeaderProgressCount>
                {mappedCount}/{totalFieldCount}
              </HeaderProgressCount>
            </HeaderProgress>
          ) : undefined
        }
      />
      <Wrap>
        <SelectPortalHost ref={setSelectPortal} />
        <PageWrapperTwoColumn
          minWidth="1030px"
          id="spotter-fields"
          padding="0"
          defaultRatio={0.72}
          stickyTop="var(--spotterHeaderHeight, 98px)"
        >
          <Main>
            <Note>
              <NoteRule />
              <div>{SPOTTER_STRINGS.fields.note}</div>
            </Note>

            <SearchAddRow>
              <SearchAddRowBody>
                <SearchWrap>
                  <Input
                    noPadding
                    icon={Search}
                    placeholder={SPOTTER_STRINGS.fields.searchPlaceholder}
                    value={search}
                    onChange={setSearch}
                  />
                </SearchWrap>
                <AddOptionSelect
                  placeholder={SPOTTER_STRINGS.fields.addFieldButton}
                  options={addFieldOptions}
                  onSelect={handleAddField}
                  portalTarget={selectPortal}
                />
              </SearchAddRowBody>
            </SearchAddRow>

            <FieldRows $disabled={saveMutation.isLoading}>
              {order.length === 0 ? (
                <NoFieldsMessage>
                  {SPOTTER_STRINGS.fields.noFieldsMessage}
                </NoFieldsMessage>
              ) : (
                order
                  .filter((key) => visibleKeys.has(key))
                  .map((key) => renderFieldRow(key))
              )}
            </FieldRows>
          </Main>
          <Aside>
            <AsideChangesPanel>
              <SpotterChangesPanel {...changesProps} />
            </AsideChangesPanel>
            <Section title={SPOTTER_STRINGS.fields.preview.cardTitle}>
              {previewRows.length === 0 ? (
                <PreviewEmpty>
                  {SPOTTER_STRINGS.fields.preview.empty}
                </PreviewEmpty>
              ) : (
                previewRows.map(({ key, label, values }) => (
                  <PreviewRow key={key}>
                    <PreviewField>{label}</PreviewField>
                    <PreviewValues>
                      {values.map((v) => (
                        <TagChip key={v}>{v}</TagChip>
                      ))}
                    </PreviewValues>
                  </PreviewRow>
                ))
              )}
            </Section>
            <Section
              title={SPOTTER_STRINGS.fields.unusedSources.cardTitle}
              buttons={
                <CountEm>
                  {unusedSources.length ||
                    SPOTTER_STRINGS.fields.unusedSources.allUsed}
                </CountEm>
              }
            >
              {unusedSources.length === 0 ? (
                <PreviewEmpty>
                  {SPOTTER_STRINGS.fields.unusedSources.empty}
                </PreviewEmpty>
              ) : (
                unusedSourceGroups.map(([group, groupFields]) => (
                  <TrayGroup key={group}>
                    <TrayGroupLabel>{toTitleCase(group)}</TrayGroupLabel>
                    <Tray>
                      {groupFields.map((f) => (
                        <TrayChip key={f.key}>{f.label}</TrayChip>
                      ))}
                    </Tray>
                  </TrayGroup>
                ))
              )}
            </Section>
          </Aside>
        </PageWrapperTwoColumn>
      </Wrap>
    </Page>
  );
};

export default FieldsScreen;
